import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { Feature } from '@app/core/interfaces/c5/feature';
import { FeatureName } from '@app/core/interfaces/c5/feature-name';
import { NumberRange } from '@app/core/interfaces/c5/number-range';
import { RoutingGroup } from '@app/core/interfaces/c5/routing-group';
import { EnterpriseOption } from '@app/core/interfaces/enterprise-option';
import { EnterpriseOptionType } from '@app/core/interfaces/enterprise-option-type';
import { NewTrunk } from '@app/core/interfaces/portal/new-trunk';
import { NumberRangeUpdate } from '@app/core/interfaces/portal/number-range-update';
import { TeamsTrunkUpdate } from '@app/core/interfaces/portal/teams-trunk-update';
import { TrunkUpdate } from '@app/core/interfaces/portal/trunk-update';
import { ScreenNumber } from '@app/core/interfaces/screen-number';
import { Site } from '@app/core/interfaces/site';
import { TeamsDomainSuggestion } from '@app/core/interfaces/teams-domain-suggestion';
import { TeamsSBC } from '@app/core/interfaces/teams-sbc';
import { TrunkCommon } from '@app/core/interfaces/trunk-common';
import { TrunkCredentials } from '@app/core/interfaces/trunk-credentials';
import { isTeamsTrunk, TrunkProfile } from '@app/core/interfaces/trunk-profile';
import { SecurityPermissionName } from '@app/core/models/permission';
import { Registration } from '@app/core/models/registration';
import { TrunkRepository } from '@app/core/repositories/trunk.repository';
import {
  ApplicationEventService,
  OptionalFeatureChangedEvent,
  TrunkUsersChangedAfterBulkActionEvent,
  TrunkUsersChangedEvent,
  UserAssignedToTeamsTrunkEvent,
} from '@app/core/services/application-events';
import { RouterService } from '@app/routing/router.service';
import { EnhancedEndUser } from '@app/shared/models/enhanced-end-user';
import { CachedHelperService } from '@app/shared/services/cached-helper.service';
import { EnterpriseContextService } from '@app/shared/services/context/enterprise-context.service';
import { ServiceProviderContextService } from '@app/shared/services/context/service-provider-context.service';
import { EnterpriseAuthorizationService } from '@app/shared/services/enterprise-authorization.service';
import { SharedService } from '@app/shared/services/shared.service';
import { TrunkUtilService } from '@app/shared/services/trunk-util.service';
import { cached, Cached, cachedGroup } from '@app/utils/cached';
import { groupBy, sortBy } from '@app/utils/common';
import { formatNumberRange } from '@app/utils/format-number-range';
import { wrapError } from '@app/utils/rxjs';
import { StringMap } from '@app/utils/string-map';
import { extendUserWithExtension } from '@app/utils/user';
import { CallBarringViewModel } from '@business/call-barring/models/call-barring-view-model';
import { FeatureStatus } from '@business/feature/interfaces/feature-status';
import { NumberRangeFeaturesViewModel } from '@business/trunk/models/number-range-features-view-model';
import { NumberRangeViewModel } from '@business/trunk/models/number-range-view-model';
import { NumberRangesViewModel } from '@business/trunk/models/number-ranges-view-model';
import { TrunkCallBarringViewModel } from '@business/trunk/models/trunk-call-barring-view-model';
import { TrunkDetailViewModel } from '@business/trunk/models/trunk-detail-view-model';
import { TrunkFeaturesViewModel } from '@business/trunk/models/trunk-features-view-model';
import { TrunkViewModel } from '@business/trunk/models/trunk-view-model';
import { TrunksViewModel } from '@business/trunk/models/trunks-view-model';
import { NumberRangeRepository } from '@business/trunk/services/number-range.repository';
import { TrunkCallBarringRepository } from '@business/trunk/services/trunk-call-barring-repository.service';
import { availableAsScreenNumberForSystemRange } from '@business/trunk/utils/available-as-screen-number-for-system-range';
import produce from 'immer';
import { combineLatest, iif, merge, Observable, of } from 'rxjs';
import { delayWhen, first, map, mergeMap, shareReplay, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class TrunkService {
  constructor(
    private applicationEventService: ApplicationEventService,
    private authenticationService: AuthenticationService,
    private cachedHelperService: CachedHelperService,
    private enterpriseAuthorizationService: EnterpriseAuthorizationService,
    private serviceProviderContextService: ServiceProviderContextService,
    private enterpriseContextService: EnterpriseContextService,
    private numberRangeRepository: NumberRangeRepository,
    private routerService: RouterService,
    private sharedService: SharedService,
    private trunkCallBarringRepository: TrunkCallBarringRepository,
    private trunkRepository: TrunkRepository,
    private trunkUtilService: TrunkUtilService
  ) {
    merge(
      this.applicationEventService.on$(OptionalFeatureChangedEvent),
      this.applicationEventService.on$(UserAssignedToTeamsTrunkEvent),
      this.applicationEventService.on$(TrunkUsersChangedEvent),
      this.applicationEventService.on$(TrunkUsersChangedAfterBulkActionEvent)
    ).subscribe(() => {
      this._trunk?.reload();
    });

    this.cachedHelperService
      .routeAwareCached({
        dataProducer: () =>
          enterpriseAuthorizationService.hasEnterpriseOption$(
            EnterpriseOptionType.MicrosoftTeamsTrunkChannels
          ),
        id: ([enterpriseId]) => `enterprise:${enterpriseId}:has-teams-trunk-channels`,
        routeParameters: ['enterpriseId'],
      })
      .data$.subscribe(() => {
        this._teamsDomainSuggestion?.reload();
      });
  }

  private _callBarring: Cached<TrunkCallBarringViewModel> | null = null;

  get callBarring(): Cached<TrunkCallBarringViewModel> {
    this._callBarring ??= cachedGroup(
      [this.trunkDetails],
      ([trunkDetail]) =>
        this.trunkCallBarringRepository.list(trunkDetail.trunk._original.c5.loginName).pipe(
          map((data) => {
            const vm: TrunkCallBarringViewModel = {
              inAllowed: [],
              inDenied: [],
              outAllowed: [],
              outDenied: [],
              trunk: trunkDetail.trunk,
            };

            // map to view model
            for (let entry of data.in) {
              if (entry.access === 'DENY') {
                vm.inDenied.push({
                  ...entry,
                  anonymous: entry.userPattern.trim() === 'anonymous',
                });
              } else {
                vm.inAllowed.push(entry);
              }
            }

            for (let entry of data.out) {
              if (entry.access === 'DENY') {
                vm.outDenied.push(entry);
              } else {
                vm.outAllowed.push(entry);
              }
            }

            return vm;
          })
        ),
      {
        id: 'trunk:call-barring',
      }
    );

    return this._callBarring;
  }

  private _features: Cached<TrunkFeaturesViewModel> | null = null;

  get features(): Cached<TrunkFeaturesViewModel> {
    this._features ??= cachedGroup(
      [this.trunkDetails, this.enterpriseContextService.enterprise],
      ([trunkDetail, { defaultSite }]) => {
        return this.trunkRepository.getFeatures(trunkDetail.trunk.id).pipe(
          map((features) => {
            const trunk = trunkDetail.trunk._original.c5;

            return {
              domainName: trunkDetail.trunk.site,
              feature: {
                acdQueues: [],
                announcements: [],
                callCenter: undefined,
                validDevices: [],
                cstaDevices: [],
                endUser: {
                  domain: defaultSite,
                  isAcdQueueAgent: false,
                  isCallCenterAgent: false,
                  user: trunk as any,
                },
                featureCodes: [],
                featureProfile: undefined,
                features,
                ivrAssistants: [],
                enterpriseOptions: [],
                phoneBook: [],
                richPresenceActive: false,
                rooms: [],
                serviceUsers: [],
              },
              trunk: trunkDetail.trunk,
            } as TrunkFeaturesViewModel;
          })
        );
      },
      {
        id: 'trunk:features',
      }
    );

    return this._features;
  }

  private _list: Cached<TrunksViewModel> | null = null;

  get list(): Cached<TrunksViewModel> {
    this._list ??= cachedGroup(
      [
        this.profiles,
        this.teamsDomainSuggestion,
        this.enterpriseContextService.enterprise,
        this.enterpriseContextService.screenNumbers,
        this.enterpriseContextService.enterpriseOptionsSource,
        this.enterpriseContextService.sites,
        this.teamsSBCs,
      ],
      ([
        profiles,
        teamsDomainSuggestion,
        enterprise,
        screenNumbers,
        enterpriseOptions,
        { sites, siteMap },
        teamsSBCs,
      ]) => {
        return this.trunkRepository.list().pipe(
          map((trunks) => {
            const availableTeamsTrunkChannels =
              enterpriseOptions
                .filter((option) => option.name === EnterpriseOptionType.MicrosoftTeamsTrunkChannels)
                .map((option) => option.available)[0] ?? 0;

            const availableTrunkChannels =
              enterpriseOptions
                .filter((option) => option.name === EnterpriseOptionType.BusinessTrunkChannels)
                .map((option) => option.available)[0] ?? 0;

            const viewModels: TrunkViewModel[] = trunks
              .map((businessTrunk) =>
                this.mapBusinessTrunkToViewModel(
                  businessTrunk,
                  siteMap,
                  screenNumbers,
                  enterpriseOptions,
                  teamsSBCs,
                  profiles
                )
              )
              .sort(sortBy(['id']));

            // Exclude screen numbers that are in use or cannot be used as a system range.
            const availableScreenNumbers: ScreenNumber[] = this.getAvailableScreenNumbers(
              viewModels,
              screenNumbers
            );

            // Only one teams trunk per page is allowed
            const addTeamsTrunkDisabledReason = this.checkIfTeamsTrunksCanBeCreated(
              viewModels,
              sites,
              availableScreenNumbers
            );

            const addTrunkDisabledReason: string | null =
              this.authenticationService.hasPermission(
                SecurityPermissionName.MANAGE_TRUNK_CUSTOM_NUMBER_RANGE
              ) === false && availableScreenNumbers.length === 0
                ? 'noMoreScreenNumbers'
                : null;

            const viewModel: TrunksViewModel = {
              addTeamsTrunkDisabledReason,
              addTrunkDisabledReason,
              availableScreenNumbers,
              availableTrunkChannels,
              availableTeamsTrunkChannels,
              defaultSite: enterprise.defaultSite,
              isMultiSite: sites.length > 1,
              profiles,
              screenNumbers: screenNumbers,
              sites,
              teamsDomainSuggestion,
              trunks: viewModels,
            };

            return viewModel;
          })
        );
      },
      {
        id: 'trunk:list',
      }
    );

    return this._list;
  }

  private _numberRanges: Cached<NumberRangesViewModel> | null = null;

  get numberRanges(): Cached<NumberRangesViewModel> {
    this._numberRanges ??= cachedGroup(
      [this.trunkDetails, this.list, this.enterpriseContextService.screenNumbers],
      ([viewModel, trunksViewModel, screenNumbers]) => {
        const numberRangesOfOtherTrunks = trunksViewModel.trunks
          .filter((trunk) => trunk.id !== viewModel.trunk.id)
          .flatMap((trunk) => trunk.numberRanges)
          .map((numberRangeViewModel) => numberRangeViewModel._original);

        const availableScreenNumbers = screenNumbers.filter(
          (screenNumber) => screenNumber.siteId == null || screenNumber.siteId === viewModel.trunk.siteId
        );

        return of({
          featureRanges: viewModel.trunk.featureRanges,
          numberRanges: viewModel.trunk.numberRanges,
          screenNumbers: this.trunkUtilService.filterAvailableScreenNumber({
            numberRanges: numberRangesOfOtherTrunks,
            screenNumbers: availableScreenNumbers,
          }),
          trunk: viewModel.trunk,
        } as NumberRangesViewModel);
      },
      {
        id: 'trunk:numbers-ranges',
      }
    );

    return this._numberRanges;
  }

  private _numberRangeFeatures: Cached<NumberRangeFeaturesViewModel> | null = null;

  get numberRangeFeatures(): Cached<NumberRangeFeaturesViewModel> {
    this._numberRangeFeatures = cachedGroup(
      [this.trunkDetails, this.enterpriseContextService.enterprise],
      ([trunkDetail, { defaultSite }]) => {
        const numberRangeId = parseInt(this.routerService.currentRouteData.params['numberRangeId']);

        return this.numberRangeRepository.getFeatures(numberRangeId).pipe(
          map((features) => {
            const trunkViewModel = trunkDetail.trunk;
            const trunk = trunkViewModel._original.c5;

            const viewModel: NumberRangeFeaturesViewModel = {
              domainName: trunkViewModel.site,
              feature: {
                acdQueues: [],
                announcements: [],
                callCenter: undefined,
                validDevices: [],
                cstaDevices: [],
                endUser: {
                  domain: defaultSite,
                  isAcdQueueAgent: false,
                  isCallCenterAgent: false,
                  user: trunk as any,
                },
                featureCodes: [],
                featureProfile: undefined,
                features,
                ivrAssistants: [],
                enterpriseOptions: [],
                phoneBook: [],
                richPresenceActive: false,
                rooms: [],
                serviceUsers: [],
              },
              numberRange: trunkViewModel.featureRanges.find(
                (numberRange) => numberRange._original.id === numberRangeId
              ),
              trunk: trunkViewModel,
            };

            return viewModel;
          }),
          wrapError('trunk-service:get-number-range-features')
        );
      },
      {
        id: 'trunk:number-range-features',
      }
    );

    return this._numberRangeFeatures;
  }

  private _profiles: Cached<TrunkProfile[]>;

  get profiles(): Cached<TrunkProfile[]> {
    this._profiles ??= this.cachedHelperService.routeAwareCached({
      dataProducer: ([]) =>
        this.trunkRepository
          .listProfiles({
            includeJson: true,
          })
          .pipe(wrapError('trunk-service:list-profiles')),
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:trunk-profiles`,
      routeParameters: ['enterpriseId'],
    });

    return this._profiles;
  }

  private _teamsSBCs: Cached<TeamsSBC[]> | null = null;

  get teamsSBCs(): Cached<TeamsSBC[]> {
    this._teamsSBCs ??= cached({
      dataProducer: () =>
        iif(
          () => this.authenticationService.userIsServiceProviderAdmin(),
          this.trunkRepository.listTeamsSBCs().pipe(wrapError('trunk-service:teams-SBCs')),
          of([])
        ),
      id: 'trunk:teams-SBCs',
    });

    return this._teamsSBCs;
  }

  private _teamsDomainSuggestion: Cached<TeamsDomainSuggestion | null> | null = null;

  get teamsDomainSuggestion(): Cached<TeamsDomainSuggestion> {
    this._teamsDomainSuggestion ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () =>
        this.authenticationService.ifPermission$(
          SecurityPermissionName.MANAGE_TEAMS_TRUNKS,
          this.enterpriseAuthorizationService
            .hasEnterpriseOption$(EnterpriseOptionType.MicrosoftTeamsTrunkChannels)
            .pipe(
              mergeMap((hasTeamsTrunkChannels) => {
                return hasTeamsTrunkChannels ? this.trunkRepository.getTeamsDomainSuggestion() : of(null);
              }),
              wrapError('trunk-service:teams-domain-suggestion')
            ),
          null
        ),
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:teams-domain-suggestion`,
      routeParameters: ['enterpriseId'],
    });

    return this._teamsDomainSuggestion;
  }

  private _trunkDetails: Cached<TrunkDetailViewModel> | null = null;

  get trunkDetails(): Cached<TrunkDetailViewModel> {
    this._trunkDetails ??= cachedGroup(
      [
        this.trunk,
        this.enterpriseContextService.sites,
        this.enterpriseContextService.screenNumbers,
        this.enterpriseContextService.enterpriseOptionsSource,
        this.teamsSBCs,
        this.profiles,
        this.enterpriseContextService.enterprise,
        this.serviceProviderContextService.resellerSBCs,
      ],
      ([
        { trunk, routingGroups },
        { siteMap },
        numbers,
        enterpriseOptions,
        sbcs,
        profiles,
        enterprise,
        resellerSBCs,
      ]) => {
        const enterpriseResellerTrunkSbcId = enterprise.local?.resellerTrunkSbcId;
        const enterpriseResellerTrunkRedundantSbcId = enterprise.local?.resellerTrunkRedundantSbcId;
        const trunkSBC =
          enterpriseResellerTrunkSbcId != null
            ? resellerSBCs.find((rscb) => rscb.id === enterpriseResellerTrunkSbcId)
            : null;
        const trunkRedundantSBC =
          enterpriseResellerTrunkRedundantSbcId != null
            ? resellerSBCs.find((rscb) => rscb.id === enterpriseResellerTrunkRedundantSbcId)
            : null;
        return of({
          routingGroups: routingGroups.filter((routingGroup) => routingGroup.domainId === trunk.c5.domainId),
          trunk: this.mapBusinessTrunkToViewModel(trunk, siteMap, numbers, enterpriseOptions, sbcs, profiles),
          trunkSBC,
          trunkRedundantSBC,
        });
      },
      {
        id: 'trunk:details',
        watchSourcesLoadingState: true,
      }
    );

    return this._trunkDetails;
  }

  private _trunk: Cached<{
    routingGroups: RoutingGroup[];
    trunk: TrunkCommon;
  }> | null = null;

  private get trunk() {
    this._trunk ??= this.cachedHelperService.routeAwareCached({
      dataProducer: ([trunkId]) => {
        const trunk$: Observable<TrunkCommon> = this.trunkRepository.get(trunkId).pipe(shareReplay(1));
        const routingGroups$: Observable<RoutingGroup[]> = trunk$.pipe(
          mergeMap((trunk) => this.sharedService.fetchRoutingGroups(trunk.c5.domainId))
        );

        return combineLatest([trunk$, routingGroups$]).pipe(
          map(([trunk, routingGroups]) => {
            return {
              routingGroups,
              trunk,
            };
          }),
          wrapError('trunk-service:get-trunk')
        );
      },
      id: ([trunkId, enterpriseId]) => `enterprise:${enterpriseId}:trunk-${trunkId}`,
      rememberTtl: 10_000,
      routeParameters: ['trunkId', 'enterpriseId'],
    });

    return this._trunk!;
  }

  add(model: NewTrunk): Observable<TrunkCommon> {
    return this.trunkRepository.add(model).pipe(
      delayWhen(() => this.list.reload(false)),
      wrapError('trunk-service:add')
    );
  }

  addCallBarring(entry: CallBarringViewModel, userId: string, direction: string): Observable<void> {
    return this.trunkCallBarringRepository
      .add(userId, direction, entry)
      .pipe(wrapError('trunk-service:add-call-barring'));
  }

  addNumberRange(numberRange: NumberRangeUpdate, userId: number): Observable<NumberRange> {
    return this.numberRangeRepository.add(numberRange, userId).pipe(
      delayWhen(() => this.trunk.reload()),
      tap(() => this._list?.reload()),
      wrapError('trunk-service:add-number-range')
    );
  }

  addTeamsTrunk(teamsTrunk: NewTrunk): Observable<TrunkCommon> {
    return this.trunkRepository.addTeamsTrunk(teamsTrunk).pipe(
      delayWhen(() => this.list.reload(false)),
      wrapError('trunk-service:add-teams-trunk')
    );
  }

  assignUserToTrunk(trunkId: string, loginName: string, force?: boolean): Observable<void> {
    return this.trunkRepository.assignUserToTrunk(trunkId, loginName, force).pipe(
      tap(() => {
        this.applicationEventService.emit(
          TrunkUsersChangedEvent({
            loginName,
            trunkId,
            type: 'add',
          })
        );
      }),
      wrapError('trunk-service:assign-user-to-trunk')
    );
  }

  delete(id: string): Observable<void> {
    return this.trunkRepository.delete(id).pipe(
      delayWhen(() => this.list.reload(false)),
      tap(() => {
        this._trunk?.clear();
      }),
      wrapError('trunk-service:delete')
    );
  }

  deleteCallBarring(userId: string, direction: string, id: number): Observable<void> {
    return this.trunkCallBarringRepository
      .delete(userId, direction, id)
      .pipe(wrapError('trunk-service:delete-call-barring'));
  }

  deleteNumberRange(id: number): Observable<void> {
    return this.numberRangeRepository.delete(id).pipe(
      delayWhen(() => this.trunk.reload()),
      tap(() => this._list?.reload()),
      wrapError('trunk-service:delete-number-range')
    );
  }

  deleteRegistration(trunkId: string, registrationId: string): Observable<void> {
    return this.trunkRepository.deleteRegistration(trunkId, registrationId).pipe(
      delayWhen(() => this.trunk.reload()),
      tap(() => this._list?.reload()),
      wrapError('trunk-service:delete-registration')
    );
  }

  deleteTeamsTrunk(id: string): Observable<void> {
    return this.trunkRepository.deleteTeamsTrunk(id).pipe(
      delayWhen(() => this.list.reload(false)),
      tap(() => {
        this._trunk?.clear();
      }),
      wrapError('trunk-service:delete-teams-trunk')
    );
  }

  readPassword(trunkId: string): Observable<string> {
    return this.trunkRepository.readPassword(trunkId).pipe(wrapError('trunk-service:read-password'));
  }

  removeUserFromTrunk(trunkId: string, loginName: string): Observable<void> {
    return this.trunkRepository.removeUserFromTrunk(trunkId, loginName).pipe(
      tap(() => {
        this.applicationEventService.emit(
          TrunkUsersChangedEvent({
            loginName,
            trunkId,
            type: 'remove',
          })
        );
      })
    );
  }

  update(id: string, update: TrunkUpdate | TeamsTrunkUpdate, teamsTrunk: boolean): Observable<any> {
    return iif(
      () => teamsTrunk,
      this.trunkRepository.updateTeamsTrunkData(id, update as TeamsTrunkUpdate),
      this.trunkRepository.update(id, update)
    ).pipe(
      delayWhen(() => combineLatest([this.list.reload(), this.trunk.reload()])),
      wrapError('trunk-service:update')
    );
  }

  updateCallBarring(entry: CallBarringViewModel, userId: string, direction: string): Observable<void> {
    return this.trunkCallBarringRepository
      .update(userId, direction, entry)
      .pipe(wrapError('trunk-service:update-call-barring'));
  }

  updateCredentials(id: string, credentials: TrunkCredentials): Observable<TrunkCommon> {
    return this.trunkRepository.updateCredentials(id, credentials).pipe(
      delayWhen(() => combineLatest([this.list.reload(), this.trunk.reload()])),
      wrapError('trunk-service:update-credentials')
    );
  }

  updateFeature(trunkId: string, feature: Feature): Observable<Feature> {
    return this.trunkRepository.updateFeature(trunkId, feature).pipe(
      tap((updatedFeature) => {
        const cachedFeatures = this.features;

        cachedFeatures.data$.pipe(first()).subscribe((features) => {
          const update = produce(features, (draft) => {
            Object.entries(draft.feature.features).forEach(([, _]) => {
              if (_.id === updatedFeature.id) {
                Object.assign(_, updatedFeature);
              }
            });
          });

          cachedFeatures.setData(update);

          this._list?.reload(false);
          this._trunk?.reload();
        });
      }),
      wrapError('trunk-service:update-feature')
    );
  }

  updateNumberRange(id: number, numberRange: NumberRangeUpdate): Observable<NumberRange> {
    return this.numberRangeRepository.update(id, numberRange).pipe(
      delayWhen(() => this.trunk.reload()),
      tap(() => {
        this._list?.reload(false);
      }),
      wrapError('trunk-service:update-number-range')
    );
  }

  updateNumberRangeFeature(id: number, feature: Feature): Observable<Feature> {
    return this.numberRangeRepository.updateFeature(id, feature).pipe(
      tap((updatedFeature) => {
        const cachedFeatures = this.numberRangeFeatures;

        cachedFeatures.data$.pipe(first()).subscribe((features) => {
          const update = produce(features, (draft) => {
            Object.entries(draft.feature.features).forEach(([, _]) => {
              if (_.id === updatedFeature.id) {
                Object.assign(_, updatedFeature);
              }
            });
          });

          cachedFeatures.setData(update);
        });
      }),
      wrapError('trunk-service:update-number-range-feature')
    );
  }

  validateTeamsDomain(subdomain: string): Observable<void> {
    return this.trunkRepository
      .validateTeamsDomain(subdomain)
      .pipe(wrapError('trunk-service:validate-teams-domain'));
  }

  private checkIfTeamsTrunksCanBeCreated(
    trunks: TrunkViewModel[],
    sites: Site[],
    screenNumbers: ScreenNumber[]
  ): string | null {
    const canManageCustomNumberRange = this.authenticationService.hasPermission(
      SecurityPermissionName.MANAGE_TRUNK_CUSTOM_NUMBER_RANGE
    );

    let reason: string | null = null;

    if (canManageCustomNumberRange === false && screenNumbers.length === 0) {
      reason = 'noMoreScreenNumbers';
    } else {
      const trunksPerSite = groupBy(
        trunks.filter((trunk) => trunk.isTeamsTrunk),
        (viewModel) => viewModel.siteId
      );
      const sitesWithTrunks = Object.keys(trunksPerSite);

      if (sitesWithTrunks.length === sites.length) {
        reason = 'noMoreSites';
      } else {
        const screenNumbersPerSite = groupBy(
          screenNumbers,
          (screenNumber) => screenNumber.siteId ?? 'enterprise'
        );

        const trunksPerSite = groupBy(trunks, (trunk) => trunk.siteId);

        const check = sites.map((site) => {
          const hasScreenNumbers =
            (screenNumbersPerSite[site.id]?.length > 0 || screenNumbersPerSite['enterprise']?.length > 0) ??
            false;
          const hasTeamsTrunks = trunksPerSite[site.id]?.some((trunk) => trunk.isTeamsTrunk) ?? false;

          return {
            hasScreenNumbers,
            hasTeamsTrunks,
            siteId: site.id,
          };
        });

        const canCreateTeamsTrunks = check.some(
          ({ hasScreenNumbers, hasTeamsTrunks }) => hasScreenNumbers === true && hasTeamsTrunks === false
        );

        if (canCreateTeamsTrunks === false && canManageCustomNumberRange === false) {
          reason = 'noMoreScreenNumbersForAvailableSites';
        }
      }
    }

    return reason;
  }

  /**
   * Exclude screen numbers that are in use or cannot be used as a system range.
   *
   * @param trunks List of existing trunks
   * @param screenNumbers List of screen numbers
   * @private
   */
  private getAvailableScreenNumbers(trunks: TrunkViewModel[], screenNumbers: ScreenNumber[]): ScreenNumber[] {
    return this.trunkUtilService
      .filterAvailableScreenNumber({
        numberRanges: trunks.flatMap((trunk) => trunk.numberRanges).map((viewModel) => viewModel._original),
        screenNumbers,
      })
      .filter(availableAsScreenNumberForSystemRange);
  }

  private getMaxAvailableChannels(trunkCommon: TrunkCommon, enterpriseOptions: EnterpriseOption[]): number {
    let channels: number;

    if (isTeamsTrunk(trunkCommon.db)) {
      channels =
        enterpriseOptions
          .filter((option) => option.name === EnterpriseOptionType.MicrosoftTeamsTrunkChannels)
          .map((option) => option.available)[0] ?? 0;
    } else {
      channels =
        enterpriseOptions
          .filter((option) => option.name === EnterpriseOptionType.BusinessTrunkChannels)
          .map((option) => option.available)[0] ?? 0;
    }

    return channels;
  }

  private mapBusinessTrunkToViewModel(
    trunkCommon: TrunkCommon,
    siteMap: StringMap<Site>,
    screenNumbers: ScreenNumber[],
    enterpriseOptions: EnterpriseOption[],
    teamsSBCs: TeamsSBC[],
    profiles: TrunkProfile[]
  ): TrunkViewModel {
    const { c5, db } = trunkCommon;

    const systemRange = c5.numberRanges.find((_) => _.systemRange)!;
    const numberRanges = c5.numberRanges.filter((_) => _.featureRange === false);
    const featureRanges = c5.numberRanges.filter((_) => _.featureRange);

    const availableTrunkChannels = this.getMaxAvailableChannels(trunkCommon, enterpriseOptions);
    const isServiceProviderAdmin: boolean = this.authenticationService.userIsServiceProviderAdmin();
    const permissionToManageTeamsTrunks = this.authenticationService.hasPermission(
      SecurityPermissionName.MANAGE_TEAMS_TRUNKS
    );
    const permissionToEditTrunks = this.authenticationService.hasPermission(
      SecurityPermissionName.EDIT_TRUNKS
    );

    const screenNumber = screenNumbers.find(
      (screenNumber) =>
        this.trunkUtilService.numberMatchesNumberRange(screenNumber, systemRange) ||
        numberRanges.some((numberRange) =>
          this.trunkUtilService.numberMatchesNumberRange(screenNumber, numberRange)
        )
    );

    const registrations: Registration[] = c5.registrations ?? [];

    // It is possible to set an origin code directly on the trunk user which is
    // not copied to the system range (which it probably should). Therefore, we
    // copy it here so the origin code is shown in the number range table.
    if (c5.originCode != null) {
      systemRange.originCode = c5.originCode;
    }

    const sipUri: string = (c5.devices ?? []).map((device) => device.sipUri)[0];
    const [sipUser, sipDomain] = sipUri.match(/^(.*)@(.*)$/).slice(1);
    const activeProfile = profiles.find((profile) => profile.id === trunkCommon.db.profileId);

    return {
      _original: trunkCommon,
      assignedUsers: (trunkCommon.users ?? []).map(
        (user) => extendUserWithExtension(user) as EnhancedEndUser
      ),
      availableTrunkChannels,
      canAssignUsers: screenNumber != null,
      channelsDoNotMatch: c5.specialTarget.maxCalls !== db.channels,
      description: c5.description,
      excludeIpFilter: c5.excludeIpFilter ?? false,
      features: c5.features.filter((_) => _.status === FeatureStatus.ACTIVE),
      featureRanges: featureRanges
        .map((featureRange) => this.mapNumberRangeToViewModel(featureRange))
        .sort(sortBy(['startCc', 'startNdc', 'startSn'])),
      id: db.number,
      isTeamsTrunk: isTeamsTrunk(trunkCommon.db) ?? null,
      name: formatNumberRange(systemRange),
      channels: db.channels,
      routingGroupId: c5.routingGroupId,
      isCfuActive: c5.features.some(
        (feature) =>
          feature.status === FeatureStatus.ACTIVE &&
          feature.featName === FeatureName.CALL_FORWARD_UNCONDITIONAL
      ),
      numberRanges: numberRanges
        .map((numberRange) => this.mapNumberRangeToViewModel(numberRange))
        .sort(sortBy(['startCc', 'startNdc', 'startSn'])),
      originCode: c5.originCode,
      profile: activeProfile,
      profileDifferences: this.trunkUtilService.calculateDifferences(trunkCommon, activeProfile),
      profiles,
      registrations,
      hasBlockedRegistration: registrations.some((r) => r.blocked === true),
      hasValidRegistration: registrations.some((r) => !r.blocked),
      sbc: trunkCommon.sbc,
      setupStep: db.setupStep,
      sipDomain,
      sipUri,
      sipUser,
      site: siteMap[c5.domainId]?.name,
      siteId: siteMap[c5.domainId]?.id,
      specialTarget: c5.specialTarget,
      systemRange: this.mapNumberRangeToViewModel(systemRange),
      teams: {
        canSetSBC: isServiceProviderAdmin && (permissionToManageTeamsTrunks || permissionToEditTrunks),
        canViewSBC: isServiceProviderAdmin,
        notYetActive: db.setupStep != null && db.setupStep < 2,
        teamsSBCs,
      },
      provisionedDnsSubDomain: trunkCommon.db.dnsSubdomain,
      dnsDomain: trunkCommon.dnsDomain,
      dnsTxtEntry: trunkCommon.dnsTxtEntry,
      userId: c5.id!,
    };
  }

  private mapNumberRangeToViewModel(range: NumberRange): NumberRangeViewModel {
    let plainName = range.startCc;
    if (range.startNdc != null) {
      plainName += range.startNdc;
    }
    plainName += range.startSn;

    if (range.startDdi) {
      plainName += range.startDdi;
    }

    return {
      _original: range,
      endDdi: range.endDdi,
      flexibleDdi: range.flexibleDdi,
      id: range.id,
      isFeatureRange: range.featureRange === true,
      maxDdiDigits: range.maxDdiDigits,
      name: formatNumberRange(range),
      number: formatNumberRange(range, false),
      originCode: range.originCode,
      plainName,
      portedStatus: range.ported,
      portedTime: range.portedTime,
      startCc: range.startCc,
      startDdi: range.startDdi,
      startNdc: range.startNdc,
      startSn: range.startSn,
      systemRange: range.systemRange ?? false,
    };
  }
}
